<script setup>
import { onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "",
  },
  confirmButtonText: {
    type: String,
    default: "Aceptar",
  },
  cancelButtonText: {
    type: String,
    default: "Cancelar",
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  confirmButtonClass: {
    type: String,
    default: "btn btn-xs ms-1 bg-gradient-primary",
  },
  cancelButtonClass: {
    type: String,
    default: "btn btn-xs btn-outline-primary me-1",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "440px",
  },
});

const emit = defineEmits(["update:modelValue", "confirm"]);
const store = useStore();
const bootstrap = store.state.bootstrap;
const modalRef = ref(null);

onMounted(() => {
  if (!props.modelValue) return;
  const modal = bootstrap?.Modal.getOrCreateInstance(modalRef.value?.$el);
  modal?.show();
});

watch(
  () => props.modelValue,
  (value) => {
    const modal = bootstrap?.Modal.getOrCreateInstance(modalRef.value?.$el);
    if (!modal) return;
    if (value) {
      modal.show();
      return;
    }
    modal.hide();
  }
);

function closeModal() {
  modalRef.value?.closeModal();
}

function onHide() {
  emit("update:modelValue", false);
}

function onConfirm() {
  closeModal();
  emit("confirm");
}
</script>

<template>
  <modal-base
    ref="modalRef"
    :width="width"
    :show-default-footer-close="false"
    @hide="onHide"
  >
    <template #header>
      <h6 class="modal-title mb-0">Confirmar</h6>
    </template>

    <template #body>
      <div class="confirm-modal-body">
        <p v-if="title" class="confirm-modal-title">
          {{ title }}
        </p>
        <p v-if="text" class="confirm-modal-text mb-0">
          {{ text }}
        </p>
      </div>
    </template>

    <template #footer>
      <button
        v-if="showCancelButton"
        type="button"
        :class="cancelButtonClass"
        data-bs-dismiss="modal"
        @click="closeModal"
      >
        {{ cancelButtonText }}
      </button>
      <argon-loading-button
        :class="confirmButtonClass"
        :loading="loading"
        :label="confirmButtonText"
        @click="onConfirm"
      />
    </template>
  </modal-base>
</template>

<style scoped>
.confirm-modal-body {
  display: grid;
  gap: 0.35rem;
}

.confirm-modal-title {
  color: var(--color-text-primary);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
}

.confirm-modal-text {
  color: var(--color-text-secondary);
  font-size: 0.82rem;
  line-height: 1.45;
}
</style>
